import React from 'react';
import { Info, Zap, Clock, Wifi } from 'lucide-react';

interface VideoUploadTipsProps {
  fileSize: number;
  className?: string;
}

const VideoUploadTips: React.FC<VideoUploadTipsProps> = ({ 
  fileSize, 
  className = '' 
}) => {
  const sizeGB = fileSize / (1024 * 1024 * 1024);
  
  const getUploadTimeEstimate = () => {
    // Estimates based on common internet speeds
    if (sizeGB > 15) return "30-60 minutes";
    if (sizeGB > 5) return "15-30 minutes";
    if (sizeGB > 1) return "5-15 minutes";
    return "< 5 minutes";
  };

  const getOptimizationTips = () => {
    if (sizeGB > 10) {
      return [
        "Consider compressing your 4K video before upload",
        "Use H.264 or H.265 codec for better compression",
        "Ensure stable internet connection - avoid peak usage times",
        "Close other bandwidth-heavy applications",
        "Consider uploading overnight or during off-peak hours"
      ];
    } else if (sizeGB > 5) {
      return [
        "Ensure stable internet connection",
        "Close other applications using bandwidth",
        "Consider H.265 codec for smaller file sizes"
      ];
    } else {
      return [
        "Your file size is optimal for quick upload",
        "Maintain stable internet connection for best results"
      ];
    }
  };

  if (sizeGB < 1) return null; // Don't show tips for small files

  return (
    <div className={`bg-blue-50 border border-blue-200 rounded-lg p-4 ${className}`}>
      <div className="flex items-start space-x-3">
        <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <h4 className="text-sm font-semibold text-blue-900 mb-2">
            4K/HD Video Upload Tips
          </h4>
          
          <div className="space-y-3">
            {/* Upload Time Estimate */}
            <div className="flex items-center space-x-2 text-sm text-blue-800">
              <Clock className="w-4 h-4" />
              <span>Estimated upload time: <strong>{getUploadTimeEstimate()}</strong></span>
            </div>
            
            {/* Connection Requirements */}
            <div className="flex items-center space-x-2 text-sm text-blue-800">
              <Wifi className="w-4 h-4" />
              <span>Recommended: 10+ Mbps upload speed for smooth transfer</span>
            </div>
            
            {/* Optimization Tips */}
            <div className="space-y-1">
              <div className="flex items-center space-x-2 text-sm font-medium text-blue-900">
                <Zap className="w-4 h-4" />
                <span>Optimization Tips:</span>
              </div>
              <ul className="ml-6 space-y-1 text-sm text-blue-700">
                {getOptimizationTips().map((tip, index) => (
                  <li key={index} className="list-disc list-outside">
                    {tip}
                  </li>
                ))}
              </ul>
            </div>
            
            {sizeGB > 15 && (
              <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-md">
                <p className="text-sm text-amber-800">
                  <strong>Very Large File Notice:</strong> Files over 15GB may take significant time to upload. 
                  Consider splitting into smaller segments or compressing if possible.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoUploadTips;